import { useState, useEffect } from 'react'
import { X, Search, Loader2 } from 'lucide-react'
import { useSearchReceiptsForRefund } from '@/api/receipts'
import { formatCurrency } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useDebounce } from '@/hooks'

interface RefundModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onReceiptSelected: (receipt: any) => void
}

export function RefundModal({ open, onOpenChange, onReceiptSelected }: RefundModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const { showToast } = useUIStore()

  const {
    data: searchResults = [],
    isLoading: isSearching,
    refetch,
  } = useSearchReceiptsForRefund(debouncedSearchQuery, open && debouncedSearchQuery.length >= 2)

  useEffect(() => {
    if (open) {
      void refetch()
    } else {
      setSearchQuery('')
    }
  }, [open, refetch])

  const handleSelectReceipt = (receipt: any) => {
    // Split refunds (refund-integrity, portal waio-36-birchmount-refunds):
    // previously-refunded receipts are selectable -- the BE seeds only the
    // REMAINING quantities, and the caller (FunctionsTab / OrderDetail)
    // guards the two unsafe cases: nothing left, or an old BE that would
    // reseed the full cart. The hard "Already Refunded" block lived here;
    // it was the only thing standing in for real per-item accounting.
    if (receipt.return_number || receipt.return_total || receipt.return_receipt_id) {
      showToast({
        type: 'info',
        title: 'Previously Refunded',
        message: 'Only the remaining items will be available to refund.',
      })
    }
    onReceiptSelected(receipt)
    onOpenChange(false)
  }

  const getReturnReference = (receipt: any): string | null => {
    const ref = receipt.return_number ?? receipt.return_receipt_id ?? null
    if (ref === null || ref === undefined) return null
    const clean = String(ref).trim()
    if (!clean) return null
    // Backend may occasionally include trailing punctuation in formatted fields.
    return clean.replace(/[?]+$/, '')
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Select source receipt
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by receipt number, customer name, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-0 outline-none"
                autoFocus
              />
            </div>

            {isSearching && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            )}

            {!isSearching && searchResults.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No receipts found
              </div>
            )}

            {!isSearching && searchResults.length > 0 && (
              <div className="space-y-2">
                {searchResults.map((receipt) => {
                  const returnRef = getReturnReference(receipt)
                  const alreadyRefunded = Boolean(returnRef || receipt.return_total)
                  return (
                  <button
                    key={receipt.id}
                    onClick={() => handleSelectReceipt(receipt)}
                    className={`w-full p-4 border-2 rounded-xl transition-all text-left ${
                      alreadyRefunded
                        ? 'border-amber-300 bg-amber-50/60 hover:border-amber-400 hover:bg-amber-50'
                        : 'border-gray-200 hover:border-green-500 hover:bg-green-50'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm text-gray-500">
                          {receipt.timestamp && new Date(receipt.timestamp).toLocaleString()}
                        </div>
                        <div className="font-medium text-gray-900">
                          #{receipt.number}
                          {returnRef && (
                            <> {' \u2190 '} #{returnRef}</>
                          )}
                        </div>
                        {receipt.customer && (
                          <div className="text-sm text-gray-500">
                            {receipt.customer.display_name || receipt.customer}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">
                          {formatCurrency(receipt.totals?.total || receipt.total || 0)}
                        </div>
                        {(receipt.return_number || receipt.return_total) && (
                          <div className="text-xs text-amber-600">
                            Previously refunded — remaining items only
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                )})}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
